import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  BarChart3, Percent, Tag, Users, TrendingUp, Download, Plus, Pencil, Trash2,
  Calculator, Settings, ChevronUp, ChevronDown, RefreshCw, FileSpreadsheet,
  FileText, ToggleLeft, ToggleRight, AlertTriangle, DollarSign, Database
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── API Helpers ───────────────────────────────────────────────────────
const api = {
  getOverview: () => axios.get(`${API}/commission/overview`),
  getSettings: () => axios.get(`${API}/commission/settings`),
  putSettings: (d) => axios.put(`${API}/commission/settings`, d),
  getRules: () => axios.get(`${API}/commission/rules`),
  postRule: (d) => axios.post(`${API}/commission/rules`, d),
  putRule: (id, d) => axios.put(`${API}/commission/rules/${id}`, d),
  deleteRule: (id) => axios.delete(`${API}/commission/rules/${id}`),
  getCodes: () => axios.get(`${API}/commission/codes`),
  postCode: (d) => axios.post(`${API}/commission/codes`, d),
  putCode: (id, d) => axios.put(`${API}/commission/codes/${id}`, d),
  deleteCode: (id) => axios.delete(`${API}/commission/codes/${id}`),
  getTransactions: (p) => axios.get(`${API}/commission/transactions`, { params: p }),
  getSellers: () => axios.get(`${API}/commission/sellers`),
  setSeller: (uid, d) => axios.put(`${API}/admin/users/${uid}/seller`, d),
  setPO: (uid, po) => axios.put(`${API}/admin/users/${uid}/po-number`, { po_number: po }),
  calculate: (d) => axios.post(`${API}/commission/calculate`, d),
  seedData: () => axios.post(`${API}/commission/seed`),
  deleteSeed: () => axios.delete(`${API}/commission/seed`),
  getUsers: () => axios.get(`${API}/admin/users`),
  getCategories: () => axios.get(`${API}/categories/flat`),
  exportCsv: (t, p = {}) => {
    const params = new URLSearchParams({ export_type: t, ...p });
    window.open(`${API}/commission/export/csv?${params}`, '_blank');
  },
  exportPdf: (t, p = {}) => {
    const params = new URLSearchParams({ export_type: t, ...p });
    window.open(`${API}/commission/export/pdf?${params}`, '_blank');
  },
};

// ─── Stat Card ──────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, icon: Icon, accent = 'orange' }) => {
  const colors = {
    orange: 'bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400',
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400',
    green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400',
    purple: 'bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400',
  };
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold mt-1 font-mono">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2.5 rounded-lg ${colors[accent]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Rule Form Dialog ──────────────────────────────────────────────────
const RULE_TYPES = [
  { value: 'seller', label: 'Nach Verkäufer' },
  { value: 'category', label: 'Nach Kategorie' },
  { value: 'volume', label: 'Nach Transaktionsvolumen' },
  { value: 'time_period', label: 'Nach Zeitraum' },
  { value: 'group', label: 'Nach Gruppe' },
];

const RuleDialog = ({ open, onClose, onSave, editData, sellers, categories, groups }) => {
  const blank = { name: '', description: '', rule_type: 'seller', commission_rate: 8, priority: 1, active: true, expiry_date: '', seller_id: '', category_id: '', group_id: '', min_volume: '', max_volume: '', valid_from: '', valid_until: '' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(editData ? { ...blank, ...editData } : blank);
  }, [editData, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.rule_type || form.commission_rate === '') return toast.error('Pflichtfelder fehlen');
    setSaving(true);
    try {
      const payload = {
        ...form,
        commission_rate: parseFloat(form.commission_rate),
        priority: parseInt(form.priority) || 1,
        min_volume: form.min_volume ? parseFloat(form.min_volume) : null,
        max_volume: form.max_volume ? parseFloat(form.max_volume) : null,
        seller_id: form.seller_id || null,
        category_id: form.category_id || null,
        group_id: form.group_id || null,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        expiry_date: form.expiry_date || null,
      };
      await onSave(payload);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editData ? 'Regel bearbeiten' : 'Neue Regel erstellen'}</DialogTitle>
          <DialogDescription>Provisionsregel für spezielle Fälle konfigurieren</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Name *" value={form.name} onChange={e => set('name', e.target.value)} data-testid="rule-name-input" />
          <Input placeholder="Beschreibung" value={form.description} onChange={e => set('description', e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.rule_type} onValueChange={v => set('rule_type', v)}>
              <SelectTrigger data-testid="rule-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RULE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-2 border rounded-md px-3">
              <Input type="number" placeholder="Rate %" className="border-0 p-0 h-9" value={form.commission_rate} onChange={e => set('commission_rate', e.target.value)} data-testid="rule-rate-input" />
              <span className="text-muted-foreground text-sm">%</span>
            </div>
          </div>
          {form.rule_type === 'seller' && (
            <Select value={form.seller_id} onValueChange={v => set('seller_id', v)}>
              <SelectTrigger><SelectValue placeholder="Verkäufer auswählen..." /></SelectTrigger>
              <SelectContent>{sellers.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name} ({s.email})</SelectItem>)}</SelectContent>
            </Select>
          )}
          {form.rule_type === 'category' && (
            <Select value={form.category_id} onValueChange={v => set('category_id', v)}>
              <SelectTrigger><SelectValue placeholder="Kategorie auswählen..." /></SelectTrigger>
              <SelectContent>{categories.map(c => <SelectItem key={c.node_id} value={c.node_id}>{c.node_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {form.rule_type === 'group' && (
            <Select value={form.group_id} onValueChange={v => set('group_id', v)}>
              <SelectTrigger><SelectValue placeholder="Gruppe auswählen..." /></SelectTrigger>
              <SelectContent>{(groups || []).map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {form.rule_type === 'volume' && (
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Mindestbetrag (€)" value={form.min_volume} onChange={e => set('min_volume', e.target.value)} />
              <Input type="number" placeholder="Höchstbetrag (€, opt.)" value={form.max_volume} onChange={e => set('max_volume', e.target.value)} />
            </div>
          )}
          {form.rule_type === 'time_period' && (
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-muted-foreground">Gültig von</label><Input type="date" value={form.valid_from?.substring(0, 10) || ''} onChange={e => set('valid_from', e.target.value)} /></div>
              <div><label className="text-xs text-muted-foreground">Gültig bis</label><Input type="date" value={form.valid_until?.substring(0, 10) || ''} onChange={e => set('valid_until', e.target.value)} /></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="Priorität" value={form.priority} onChange={e => set('priority', e.target.value)} />
            <div><label className="text-xs text-muted-foreground">Ablaufdatum (opt.)</label><Input type="date" value={form.expiry_date?.substring(0, 10) || ''} onChange={e => set('expiry_date', e.target.value)} /></div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Switch checked={form.active} onCheckedChange={v => set('active', v)} id="rule-active" />
            <label htmlFor="rule-active" className="text-sm">Regel aktiv</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-rule-btn">{saving ? 'Speichern...' : 'Speichern'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Code Form Dialog ──────────────────────────────────────────────────
const CodeDialog = ({ open, onClose, onSave, editData, sellers, categories, groups }) => {
  const blank = { code: '', discount_type: 'percentage', discount_value: '', applies_to: 'all', seller_id: '', category_id: '', group_id: '', usage_limit: '', per_seller_limit: '', valid_from: '', valid_until: '', active: true, description: '' };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(editData ? { ...blank, ...editData } : blank); }, [editData, open]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.code || !form.discount_value) return toast.error('Code und Wert sind Pflicht');
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value),
        usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
        per_seller_limit: form.per_seller_limit ? parseInt(form.per_seller_limit) : null,
        seller_id: form.seller_id || null,
        category_id: form.category_id || null,
        group_id: form.group_id || null,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
      };
      await onSave(payload);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editData ? 'Code bearbeiten' : 'Neuer Rabattcode'}</DialogTitle>
          <DialogDescription>Provisionsrabatt für Verkäufer erstellen</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Code (z.B. LAUNCH2025) *" value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} data-testid="code-input" />
          <Input placeholder="Beschreibung" value={form.description} onChange={e => set('description', e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.discount_type} onValueChange={v => set('discount_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Prozentsatz (%)</SelectItem>
                <SelectItem value="fixed">Fixbetrag (€)</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 border rounded-md px-3">
              <Input type="number" placeholder="Wert *" className="border-0 p-0 h-9" value={form.discount_value} onChange={e => set('discount_value', e.target.value)} data-testid="code-value-input" />
              <span className="text-muted-foreground text-sm">{form.discount_type === 'percentage' ? '%' : '€'}</span>
            </div>
          </div>
          <Select value={form.applies_to} onValueChange={v => set('applies_to', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Verkäufer</SelectItem>
              <SelectItem value="seller">Bestimmter Verkäufer</SelectItem>
              <SelectItem value="category">Bestimmte Kategorie</SelectItem>
              <SelectItem value="group">Bestimmte Gruppe</SelectItem>
            </SelectContent>
          </Select>
          {form.applies_to === 'seller' && (
            <Select value={form.seller_id} onValueChange={v => set('seller_id', v)}>
              <SelectTrigger><SelectValue placeholder="Verkäufer..." /></SelectTrigger>
              <SelectContent>{sellers.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {form.applies_to === 'category' && (
            <Select value={form.category_id} onValueChange={v => set('category_id', v)}>
              <SelectTrigger><SelectValue placeholder="Kategorie..." /></SelectTrigger>
              <SelectContent>{categories.map(c => <SelectItem key={c.node_id} value={c.node_id}>{c.node_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {form.applies_to === 'group' && (
            <Select value={form.group_id} onValueChange={v => set('group_id', v)}>
              <SelectTrigger><SelectValue placeholder="Gruppe..." /></SelectTrigger>
              <SelectContent>{(groups || []).map(g => <SelectItem key={g.group_id} value={g.group_id}>{g.group_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder="Max. Nutzungen gesamt" value={form.usage_limit} onChange={e => set('usage_limit', e.target.value)} />
            <Input type="number" placeholder="Max. pro Verkäufer" value={form.per_seller_limit} onChange={e => set('per_seller_limit', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground">Gültig von</label><Input type="date" value={form.valid_from?.substring(0, 10) || ''} onChange={e => set('valid_from', e.target.value)} /></div>
            <div><label className="text-xs text-muted-foreground">Gültig bis</label><Input type="date" value={form.valid_until?.substring(0, 10) || ''} onChange={e => set('valid_until', e.target.value)} /></div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={v => set('active', v)} id="code-active" />
            <label htmlFor="code-active" className="text-sm">Code aktiv</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-code-btn">{saving ? 'Speichern...' : 'Speichern'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────
export default function CommissionDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [settings, setSettings] = useState({ default_rate: 10, premium_rate: 8, premium_price_monthly: 49, group_standard_rate: 7, group_enterprise_rate: 5, print_materials_rate: 10, spare_parts_rate: 8, machines_rate: 5 });
  const [rules, setRules] = useState([]);
  const [codes, setCodes] = useState([]);
  const [txns, setTxns] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [sellers, setSellers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companyGroups, setCompanyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [ruleDialog, setRuleDialog] = useState({ open: false, data: null });
  const [codeDialog, setCodeDialog] = useState({ open: false, data: null });
  const [txnFilters, setTxnFilters] = useState({ seller_id: '', date_from: '', date_to: '', page: 1 });
  const [calcInput, setCalcInput] = useState({ transaction_amount_eur: 1000, seller_id: '', category_id: '', discount_code: '' });
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [sellerManageUser, setSellerManageUser] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, s, r, c, sl, u, cats, grps] = await Promise.all([
        api.getOverview(), api.getSettings(), api.getRules(), api.getCodes(),
        api.getSellers(), api.getUsers(), api.getCategories(),
        axios.get(`${API}/groups`).catch(() => ({ data: [] }))
      ]);
      setOverview(ov.data);
      setSettings(s.data);
      setRules(r.data);
      setCodes(c.data);
      setSellers(sl.data);
      setAllUsers(u.data || []);
      setCategories(cats.data || []);
      setCompanyGroups(grps.data || []);
    } catch (e) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTxns = useCallback(async (f = txnFilters) => {
    try {
      const params = {};
      if (f.seller_id) params.seller_id = f.seller_id;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      params.page = f.page || 1;
      const res = await api.getTransactions(params);
      setTxns(res.data);
    } catch {}
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadTxns(txnFilters); }, [txnFilters, loadTxns]);

  if (!user || user.role !== 'admin') {
    navigate('/');
    return null;
  }

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.putSettings(settings);
      toast.success('Einstellungen gespeichert');
      loadAll();
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSavingSettings(false); }
  };

  const handleSaveRule = async (data) => {
    try {
      if (ruleDialog.data) {
        await api.putRule(ruleDialog.data.rule_id, data);
        toast.success('Regel aktualisiert');
      } else {
        await api.postRule(data);
        toast.success('Regel erstellt');
      }
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); throw e; }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Regel wirklich löschen?')) return;
    try { await api.deleteRule(id); toast.success('Regel gelöscht'); loadAll(); }
    catch { toast.error('Fehler beim Löschen'); }
  };

  const handleToggleRule = async (rule) => {
    try { await api.putRule(rule.rule_id, { active: !rule.active }); loadAll(); }
    catch { toast.error('Fehler'); }
  };

  const handleSaveCode = async (data) => {
    try {
      if (codeDialog.data) {
        await api.putCode(codeDialog.data.code_id, data);
        toast.success('Code aktualisiert');
      } else {
        await api.postCode(data);
        toast.success('Code erstellt');
      }
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); throw e; }
  };

  const handleDeleteCode = async (id) => {
    if (!window.confirm('Code wirklich löschen?')) return;
    try { await api.deleteCode(id); toast.success('Code gelöscht'); loadAll(); }
    catch { toast.error('Fehler beim Löschen'); }
  };

  const handleToggleCode = async (code) => {
    try { await api.putCode(code.code_id, { active: !code.active }); loadAll(); }
    catch { toast.error('Fehler'); }
  };

  const handleCalculate = async () => {
    if (!calcInput.seller_id) return toast.error('Bitte Verkäufer auswählen');
    setCalcLoading(true);
    try {
      const res = await api.calculate({ ...calcInput, transaction_amount_eur: parseFloat(calcInput.transaction_amount_eur) });
      setCalcResult(res.data);
    } catch { toast.error('Berechnung fehlgeschlagen'); }
    finally { setCalcLoading(false); }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try { await api.seedData(); toast.success('Demo-Daten erstellt'); loadAll(); }
    catch { toast.error('Fehler beim Erstellen der Demo-Daten'); }
    finally { setSeeding(false); }
  };

  const handleDeleteSeed = async () => {
    if (!window.confirm('Alle Demo-Daten wirklich löschen?')) return;
    setSeeding(true);
    try { await api.deleteSeed(); toast.success('Demo-Daten gelöscht'); loadAll(); }
    catch { toast.error('Fehler'); }
    finally { setSeeding(false); }
  };

  const handleSetSeller = async (userId, isSeller, tier) => {
    try {
      await api.setSeller(userId, { is_seller: isSeller, seller_tier: tier });
      toast.success(isSeller ? 'Als Verkäufer markiert' : 'Verkäufer-Status entfernt');
      loadAll();
    } catch { toast.error('Fehler'); }
  };

  const ruleTypeLabel = (t) => ({ seller: 'Verkäufer', category: 'Kategorie', volume: 'Volumen', time_period: 'Zeitraum', group: 'Gruppe' }[t] || t);
  const ruleTypeColor = (t) => ({ seller: 'bg-blue-100 text-blue-700', category: 'bg-green-100 text-green-700', volume: 'bg-orange-100 text-orange-700', time_period: 'bg-violet-100 text-violet-700', group: 'bg-cyan-100 text-cyan-700' }[t] || 'bg-gray-100');

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-4 gap-4">{[0,1,2,3].map(i => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-96 w-full" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="commission-dashboard">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Page Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold font-manrope flex items-center gap-2" data-testid="commission-title">
              <DollarSign className="h-6 w-6 text-orange-500" />
              Provisionsmanagement
            </h1>
            <p className="text-muted-foreground text-sm mt-1">Surplify.de · Kommissionsmodell verwalten</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSeed} disabled={seeding} data-testid="seed-btn">
              <Database className="h-3.5 w-3.5 mr-1.5" />{seeding ? '...' : 'Demo-Daten'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleDeleteSeed} disabled={seeding} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-seed-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />Demo löschen
            </Button>
            <Button size="sm" onClick={loadAll} variant="outline"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </div>

        {/* KPI Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Gesamtprovision" value={`€ ${(overview?.total_commission_eur || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })}`} sub="alle Transaktionen" icon={TrendingUp} accent="orange" />
          <StatCard label="Aktive Regeln" value={overview?.active_rules ?? 0} sub={`${overview?.total_rules ?? 0} gesamt`} icon={Settings} accent="blue" />
          <StatCard label="Aktive Codes" value={overview?.active_codes ?? 0} sub={`${overview?.total_codes ?? 0} gesamt`} icon={Tag} accent="green" />
          <StatCard label="Verkäufer" value={overview?.total_sellers ?? 0} sub={`Ø ${overview?.avg_effective_rate ?? 0}% eff. Rate`} icon={Users} accent="purple" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1 rounded-lg">
            <TabsTrigger value="overview" data-testid="tab-overview"><BarChart3 className="h-3.5 w-3.5 mr-1" />Übersicht</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="h-3.5 w-3.5 mr-1" />Einstellungen</TabsTrigger>
            <TabsTrigger value="rules" data-testid="tab-rules"><Percent className="h-3.5 w-3.5 mr-1" />Regelwerk <Badge className="ml-1 h-4 px-1.5 text-xs">{rules.length}</Badge></TabsTrigger>
            <TabsTrigger value="codes" data-testid="tab-codes"><Tag className="h-3.5 w-3.5 mr-1" />Rabattcodes <Badge className="ml-1 h-4 px-1.5 text-xs">{codes.length}</Badge></TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions"><FileText className="h-3.5 w-3.5 mr-1" />Transaktionen</TabsTrigger>
            <TabsTrigger value="calculator" data-testid="tab-calculator"><Calculator className="h-3.5 w-3.5 mr-1" />Kalkulator</TabsTrigger>
            <TabsTrigger value="sellers" data-testid="tab-sellers"><Users className="h-3.5 w-3.5 mr-1" />Verkäufer</TabsTrigger>
            <TabsTrigger value="export" data-testid="tab-export"><Download className="h-3.5 w-3.5 mr-1" />Export</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base">Monatliche Entwicklung</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(overview?.monthly_chart || []).map(m => (
                      <div key={m.month} className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground w-16 shrink-0">{m.month}</span>
                        <div className="flex-1 bg-muted rounded-full h-2 relative overflow-hidden">
                          <div className="absolute inset-y-0 left-0 bg-orange-500 rounded-full" style={{ width: `${Math.min(100, (m.commission_eur / (Math.max(...(overview?.monthly_chart || []).map(x => x.commission_eur)) || 1)) * 100)}%` }} />
                        </div>
                        <span className="text-xs font-mono w-20 text-right">€ {m.commission_eur.toFixed(2)}</span>
                        <span className="text-xs text-muted-foreground w-16 text-right">{m.transactions} Txn</span>
                      </div>
                    ))}
                    {!overview?.monthly_chart?.length && <p className="text-sm text-muted-foreground text-center py-4">Keine Daten — Demo-Daten erstellen</p>}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">Aktive Provisionsregeln</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {rules.filter(r => r.active).slice(0, 8).map(r => (
                      <div key={r.rule_id} className="flex items-center gap-2 py-1.5 border-b last:border-0">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ruleTypeColor(r.rule_type)}`}>{ruleTypeLabel(r.rule_type)}</span>
                        <span className="text-sm flex-1 truncate">{r.name}</span>
                        <span className="text-sm font-mono font-bold text-orange-600">{r.commission_rate}%</span>
                        <span className="text-xs text-muted-foreground">P{r.priority}</span>
                      </div>
                    ))}
                    {!rules.filter(r => r.active).length && <p className="text-sm text-muted-foreground text-center py-4">Keine aktiven Regeln</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Globale Provisionssätze</CardTitle>
                <CardDescription>Diese Werte gelten als Standard für alle Verkäufer ohne spezielle Regel</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 max-w-lg">
                <div>
                  <label className="text-sm font-medium">Standard-Verkäufer Provision (%)</label>
                  <p className="text-xs text-muted-foreground mb-2">Gilt für alle Verkäufer ohne Premium-Abo</p>
                  <div className="flex items-center gap-2">
                    <Input type="number" value={settings.default_rate} onChange={e => setSettings(s => ({ ...s, default_rate: parseFloat(e.target.value) }))} className="w-32" data-testid="default-rate-input" />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Premium-Verkäufer Provision (%)</label>
                  <p className="text-xs text-muted-foreground mb-2">Reduzierter Satz für Premium-Abonnenten</p>
                  <div className="flex items-center gap-2">
                    <Input type="number" value={settings.premium_rate} onChange={e => setSettings(s => ({ ...s, premium_rate: parseFloat(e.target.value) }))} className="w-32" data-testid="premium-rate-input" />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Premium-Abo Preis (€/Monat)</label>
                  <p className="text-xs text-muted-foreground mb-2">Monatliche Gebühr für das Premium-Abo</p>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">€</span>
                    <Input type="number" value={settings.premium_price_monthly} onChange={e => setSettings(s => ({ ...s, premium_price_monthly: parseFloat(e.target.value) }))} className="w-32" data-testid="premium-price-input" />
                    <span className="text-muted-foreground">/Monat</span>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <p className="text-sm font-semibold mb-3">Gruppen-Provisionssätze</p>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Gruppe Standard Provision (%)</label>
                      <p className="text-xs text-muted-foreground mb-2">Gilt für Standorte in Gruppe-Standard-Abos (€199/Mo)</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={settings.group_standard_rate} onChange={e => setSettings(s => ({ ...s, group_standard_rate: parseFloat(e.target.value) }))} className="w-32" data-testid="group-standard-rate-input" />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Gruppe Enterprise Provision (%)</label>
                      <p className="text-xs text-muted-foreground mb-2">Gilt für Standorte in Gruppe-Enterprise-Abos (€399/Mo)</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" value={settings.group_enterprise_rate} onChange={e => setSettings(s => ({ ...s, group_enterprise_rate: parseFloat(e.target.value) }))} className="w-32" data-testid="group-enterprise-rate-input" />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <p className="text-sm font-semibold mb-1">Provisionssätze nach Produktvertical</p>
                  <p className="text-xs text-muted-foreground mb-3">Überschreiben die Standardsätze je nach Listing-Typ (Print / Ersatzteile / Maschinen). Höhere Priorität als Gruppen- oder Premium-Sätze.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Druckmaterialien (%)</label>
                      <p className="text-xs text-muted-foreground mb-2">Klassisches Vertical — Etiketten, Folien, Papiere</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" step="0.1" value={settings.print_materials_rate ?? 10} onChange={e => setSettings(s => ({ ...s, print_materials_rate: parseFloat(e.target.value) }))} className="w-32" data-testid="print-materials-rate-input" />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Ersatzteile & Module (%)</label>
                      <p className="text-xs text-muted-foreground mb-2">Komponenten & Baugruppen für Druckmaschinen</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" step="0.1" value={settings.spare_parts_rate ?? 8} onChange={e => setSettings(s => ({ ...s, spare_parts_rate: parseFloat(e.target.value) }))} className="w-32" data-testid="spare-parts-rate-input" />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Druckmaschinen (%)</label>
                      <p className="text-xs text-muted-foreground mb-2">Komplette Druck- & Finishing-Maschinen — niedrigerer Satz branchenüblich</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" step="0.1" value={settings.machines_rate ?? 5} onChange={e => setSettings(s => ({ ...s, machines_rate: parseFloat(e.target.value) }))} className="w-32" data-testid="machines-rate-input" />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Farben, Lacke & Additive (%)</label>
                      <p className="text-xs text-muted-foreground mb-2">Druckfarben, Lacke und chemische Additive (Verbrauchsmaterial)</p>
                      <div className="flex items-center gap-2">
                        <Input type="number" step="0.1" value={settings.paints_rate ?? 10} onChange={e => setSettings(s => ({ ...s, paints_rate: parseFloat(e.target.value) }))} className="w-32" data-testid="paints-rate-input" />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                  </div>
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings} data-testid="save-settings-btn">
                  {savingSettings ? 'Speichern...' : 'Einstellungen speichern'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rules Tab */}
          <TabsContent value="rules">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Provisionsregelwerk</CardTitle>
                    <CardDescription>Höhere Priorität = Regel wird zuerst angewendet. Erste passende Regel gewinnt.</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => setRuleDialog({ open: true, data: null })} data-testid="add-rule-btn">
                    <Plus className="h-4 w-4 mr-1" />Neue Regel
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left">
                      <th className="pb-2 font-medium text-muted-foreground">Name</th>
                      <th className="pb-2 font-medium text-muted-foreground">Typ</th>
                      <th className="pb-2 font-medium text-muted-foreground">Rate</th>
                      <th className="pb-2 font-medium text-muted-foreground">Priorität</th>
                      <th className="pb-2 font-medium text-muted-foreground">Details</th>
                      <th className="pb-2 font-medium text-muted-foreground">Status</th>
                      <th className="pb-2"></th>
                    </tr></thead>
                    <tbody>
                      {rules.map(r => (
                        <tr key={r.rule_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`rule-row-${r.rule_id}`}>
                          <td className="py-3 font-medium">{r.name}</td>
                          <td className="py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ruleTypeColor(r.rule_type)}`}>{ruleTypeLabel(r.rule_type)}</span></td>
                          <td className="py-3 font-mono font-bold text-orange-600">{r.commission_rate}%</td>
                          <td className="py-3 font-mono text-center">{r.priority}</td>
                          <td className="py-3 text-xs text-muted-foreground max-w-[160px] truncate">
                            {r.seller_name || r.category_name || r.group_name || (r.min_volume ? `>€${r.min_volume}` : '') || (r.valid_from ? `${r.valid_from?.substring(0,10)} – ${r.valid_until?.substring(0,10)}` : '') || '–'}
                          </td>
                          <td className="py-3">
                            <button onClick={() => handleToggleRule(r)} className="flex items-center gap-1" data-testid={`toggle-rule-${r.rule_id}`}>
                              {r.active ? <ToggleRight className="h-5 w-5 text-green-500" /> : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                              <span className={`text-xs ${r.active ? 'text-green-600' : 'text-muted-foreground'}`}>{r.active ? 'Aktiv' : 'Inaktiv'}</span>
                            </button>
                          </td>
                          <td className="py-3">
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setRuleDialog({ open: true, data: r })}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDeleteRule(r.rule_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!rules.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Keine Regeln vorhanden</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <RuleDialog open={ruleDialog.open} onClose={() => setRuleDialog({ open: false, data: null })} onSave={handleSaveRule} editData={ruleDialog.data} sellers={sellers} categories={categories} groups={companyGroups} />
          </TabsContent>

          {/* Codes Tab */}
          <TabsContent value="codes">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Rabattcodes</CardTitle>
                    <CardDescription>Codes reduzieren den Provisionssatz für den Verkäufer</CardDescription>
                  </div>
                  <Button size="sm" onClick={() => setCodeDialog({ open: true, data: null })} data-testid="add-code-btn">
                    <Plus className="h-4 w-4 mr-1" />Neuer Code
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left">
                      <th className="pb-2 font-medium text-muted-foreground">Code</th>
                      <th className="pb-2 font-medium text-muted-foreground">Rabatt</th>
                      <th className="pb-2 font-medium text-muted-foreground">Gilt für</th>
                      <th className="pb-2 font-medium text-muted-foreground">Nutzungen</th>
                      <th className="pb-2 font-medium text-muted-foreground">Gültigkeit</th>
                      <th className="pb-2 font-medium text-muted-foreground">Status</th>
                      <th className="pb-2"></th>
                    </tr></thead>
                    <tbody>
                      {codes.map(c => (
                        <tr key={c.code_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`code-row-${c.code_id}`}>
                          <td className="py-3 font-mono font-bold text-sm">{c.code}</td>
                          <td className="py-3 font-bold text-orange-600">{c.discount_type === 'percentage' ? `${c.discount_value}%` : `€ ${c.discount_value}`}</td>
                          <td className="py-3 text-xs text-muted-foreground">{c.applies_to === 'all' ? 'Alle' : (c.seller_name || c.category_name || c.group_name || c.applies_to)}</td>
                          <td className="py-3 text-xs">{c.current_usage}/{c.usage_limit ?? '∞'}</td>
                          <td className="py-3 text-xs text-muted-foreground">{c.valid_until ? c.valid_until.substring(0, 10) : '–'}</td>
                          <td className="py-3">
                            <button onClick={() => handleToggleCode(c)} className="flex items-center gap-1" data-testid={`toggle-code-${c.code_id}`}>
                              {c.active ? <ToggleRight className="h-5 w-5 text-green-500" /> : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                              <span className={`text-xs ${c.active ? 'text-green-600' : 'text-muted-foreground'}`}>{c.active ? 'Aktiv' : 'Inaktiv'}</span>
                            </button>
                          </td>
                          <td className="py-3">
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setCodeDialog({ open: true, data: c })}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDeleteCode(c.code_id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!codes.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Keine Codes vorhanden</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            <CodeDialog open={codeDialog.open} onClose={() => setCodeDialog({ open: false, data: null })} onSave={handleSaveCode} editData={codeDialog.data} sellers={sellers} categories={categories} groups={companyGroups} />
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Transaktionen mit Provision</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Select value={txnFilters.seller_id || "__all__"} onValueChange={v => setTxnFilters(f => ({ ...f, seller_id: v === "__all__" ? "" : v, page: 1 }))}>
                    <SelectTrigger className="w-48"><SelectValue placeholder="Alle Verkäufer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Alle Verkäufer</SelectItem>
                      {sellers.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="date" className="w-40" value={txnFilters.date_from} onChange={e => setTxnFilters(f => ({ ...f, date_from: e.target.value, page: 1 }))} placeholder="Von" />
                  <Input type="date" className="w-40" value={txnFilters.date_to} onChange={e => setTxnFilters(f => ({ ...f, date_to: e.target.value, page: 1 }))} placeholder="Bis" />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left">
                      <th className="pb-2 font-medium text-muted-foreground">Datum</th>
                      <th className="pb-2 font-medium text-muted-foreground">Verkäufer</th>
                      <th className="pb-2 font-medium text-muted-foreground">Material</th>
                      <th className="pb-2 font-medium text-muted-foreground">Brutto</th>
                      <th className="pb-2 font-medium text-muted-foreground">Rate</th>
                      <th className="pb-2 font-medium text-muted-foreground">Provision</th>
                      <th className="pb-2 font-medium text-muted-foreground">Regel/Code</th>
                    </tr></thead>
                    <tbody>
                      {(txns.items || []).map(t => {
                        const gross = t.price_EUR || (t.value_kEUR * 1000);
                        return (
                          <tr key={t.transaction_id} className="border-b last:border-0 hover:bg-muted/30">
                            <td className="py-2.5 text-xs text-muted-foreground">{(t.created_at || '').substring(0, 10)}</td>
                            <td className="py-2.5 font-medium">{t.seller_name || t.commission_seller_id || '–'}</td>
                            <td className="py-2.5 text-xs text-muted-foreground max-w-[140px] truncate">{t.material_title || '–'}</td>
                            <td className="py-2.5 font-mono">€ {Number(gross).toFixed(2)}</td>
                            <td className="py-2.5 font-mono text-orange-600">{t.commission_rate}%</td>
                            <td className="py-2.5 font-mono font-bold">€ {Number(t.commission_amount || 0).toFixed(2)}</td>
                            <td className="py-2.5 text-xs text-muted-foreground">{t.applied_rule_name || t.applied_code || 'Standard'}</td>
                          </tr>
                        );
                      })}
                      {!txns.items?.length && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Keine Transaktionen</td></tr>}
                    </tbody>
                  </table>
                </div>
                {txns.pages > 1 && (
                  <div className="flex gap-2 mt-3 justify-end">
                    <Button size="sm" variant="outline" disabled={txnFilters.page <= 1} onClick={() => setTxnFilters(f => ({ ...f, page: f.page - 1 }))}>Zurück</Button>
                    <span className="text-sm text-muted-foreground py-2">{txnFilters.page}/{txns.pages}</span>
                    <Button size="sm" variant="outline" disabled={txnFilters.page >= txns.pages} onClick={() => setTxnFilters(f => ({ ...f, page: f.page + 1 }))}>Weiter</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calculator Tab */}
          <TabsContent value="calculator">
            <div className="grid lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Provisions-Kalkulator</CardTitle>
                  <CardDescription>Berechne den Provisionssatz für eine Transaktion</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Transaktionsbetrag (€)</label>
                    <Input type="number" value={calcInput.transaction_amount_eur} onChange={e => setCalcInput(i => ({ ...i, transaction_amount_eur: e.target.value }))} className="mt-1" data-testid="calc-amount-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Verkäufer</label>
                    <Select value={calcInput.seller_id} onValueChange={v => setCalcInput(i => ({ ...i, seller_id: v }))}>
                      <SelectTrigger className="mt-1" data-testid="calc-seller-select"><SelectValue placeholder="Verkäufer auswählen..." /></SelectTrigger>
                      <SelectContent>
                        {[...sellers, ...allUsers.filter(u => !sellers.find(s => s.user_id === u.user_id))].map(s => (
                          <SelectItem key={s.user_id} value={s.user_id}>{s.name} {sellers.find(x => x.user_id === s.user_id) ? '(Verkäufer)' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Kategorie (opt.)</label>
                    <Select value={calcInput.category_id || "__none__"} onValueChange={v => setCalcInput(i => ({ ...i, category_id: v === "__none__" ? "" : v }))}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Kategorie (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Keine</SelectItem>
                        {categories.map(c => <SelectItem key={c.node_id} value={c.node_id}>{c.node_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Rabattcode (opt.)</label>
                    <Input placeholder="Code eingeben..." value={calcInput.discount_code} onChange={e => setCalcInput(i => ({ ...i, discount_code: e.target.value.toUpperCase() }))} className="mt-1 uppercase" data-testid="calc-code-input" />
                  </div>
                  <Button onClick={handleCalculate} disabled={calcLoading} className="w-full" data-testid="calculate-btn">
                    <Calculator className="h-4 w-4 mr-2" />{calcLoading ? 'Berechne...' : 'Provision berechnen'}
                  </Button>
                </CardContent>
              </Card>
              {calcResult && (
                <Card className="border-orange-200 dark:border-orange-800">
                  <CardHeader>
                    <CardTitle className="text-base">Berechnungsergebnis</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="bg-orange-50 dark:bg-orange-950 rounded-lg p-4 text-center">
                      <p className="text-3xl font-bold font-mono text-orange-600">€ {calcResult.commission_eur?.toFixed(2)}</p>
                      <p className="text-sm text-muted-foreground mt-1">Provision bei {calcResult.final_rate?.toFixed(2)}%</p>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between py-1 border-b">
                        <span className="text-muted-foreground">Verkäufer</span>
                        <span className="font-medium">{calcResult.seller_name}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b">
                        <span className="text-muted-foreground">Tier</span>
                        <Badge variant={calcResult.seller_tier === 'premium' || calcResult.seller_tier?.startsWith('group') ? 'default' : 'outline'} className={
                          calcResult.seller_tier === 'group_enterprise' ? 'bg-violet-600' :
                          calcResult.seller_tier === 'group_standard' ? 'bg-cyan-600' :
                          calcResult.seller_tier === 'premium' ? 'bg-amber-500' : ''
                        }>
                          {calcResult.seller_tier === 'group_enterprise' ? 'Gruppe Enterprise' :
                           calcResult.seller_tier === 'group_standard' ? 'Gruppe Standard' :
                           calcResult.seller_tier === 'premium' ? 'Premium' : 'Standard'}
                        </Badge>
                      </div>
                      {calcResult.group_tier && (
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-muted-foreground">Gruppen-Tier</span>
                          <Badge variant="outline" className="text-xs">{calcResult.group_tier}</Badge>
                        </div>
                      )}
                      <div className="flex justify-between py-1 border-b">
                        <span className="text-muted-foreground">Basissatz</span>
                        <span className="font-mono">{calcResult.base_rate}%</span>
                      </div>
                      {calcResult.applied_rule && (
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-muted-foreground">Angewandte Regel</span>
                          <span className="font-medium text-blue-600">{calcResult.applied_rule.name}</span>
                        </div>
                      )}
                      {calcResult.applied_code && (
                        <div className="flex justify-between py-1 border-b">
                          <span className="text-muted-foreground">Rabattcode</span>
                          <span className="font-medium text-green-600 font-mono">{calcResult.applied_code.code}</span>
                        </div>
                      )}
                      <div className="flex justify-between py-1 border-b font-bold">
                        <span>Finaler Satz</span>
                        <span className="text-orange-600 font-mono">{calcResult.final_rate?.toFixed(2)}%</span>
                      </div>
                      <div className="flex justify-between py-1 font-bold">
                        <span>Provision</span>
                        <span className="text-orange-600 font-mono text-lg">€ {calcResult.commission_eur?.toFixed(2)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Sellers Tab */}
          <TabsContent value="sellers">
            <Card>
              <CardHeader>
                <CardTitle>Verkäufer-Verwaltung</CardTitle>
                <CardDescription>Weisen Sie Nutzern den Verkäufer-Status zu (alle Rollen möglich)</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-left">
                      <th className="pb-2 font-medium text-muted-foreground">Name</th>
                      <th className="pb-2 font-medium text-muted-foreground">E-Mail</th>
                      <th className="pb-2 font-medium text-muted-foreground">Rolle</th>
                      <th className="pb-2 font-medium text-muted-foreground">Verkäufer</th>
                      <th className="pb-2 font-medium text-muted-foreground">Tier</th>
                      <th className="pb-2 font-medium text-muted-foreground">PO-Nummer</th>
                      <th className="pb-2"></th>
                    </tr></thead>
                    <tbody>
                      {allUsers.map(u => {
                        const isSel = !!sellers.find(s => s.user_id === u.user_id);
                        const selData = sellers.find(s => s.user_id === u.user_id);
                        return (
                          <tr key={u.user_id} className="border-b last:border-0 hover:bg-muted/30" data-testid={`user-row-${u.user_id}`}>
                            <td className="py-2.5 font-medium">{u.name}</td>
                            <td className="py-2.5 text-xs text-muted-foreground">{u.email}</td>
                            <td className="py-2.5"><Badge variant="outline" className="text-xs capitalize">{u.role}</Badge></td>
                            <td className="py-2.5">
                              <Switch checked={isSel} onCheckedChange={v => handleSetSeller(u.user_id, v, selData?.seller_tier || 'standard')} data-testid={`seller-toggle-${u.user_id}`} />
                            </td>
                            <td className="py-2.5">
                              {isSel && (
                                <Select value={selData?.seller_tier || 'standard'} onValueChange={v => handleSetSeller(u.user_id, true, v)}>
                                  <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="standard">Standard</SelectItem>
                                    <SelectItem value="premium">Premium</SelectItem>
                                  </SelectContent>
                                </Select>
                              )}
                            </td>
                            <td className="py-2.5 text-xs text-muted-foreground">{selData?.po_number || '–'}</td>
                            <td className="py-2.5">
                              {isSel && (
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSellerManageUser(u)} data-testid={`manage-seller-${u.user_id}`}>PO bearbeiten</Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            {/* PO Number Edit Dialog */}
            {sellerManageUser && (
              <Dialog open={!!sellerManageUser} onOpenChange={() => setSellerManageUser(null)}>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>PO-Nummer für {sellerManageUser.name}</DialogTitle>
                    <DialogDescription>Purchase Order Referenz für Abrechnungsdokumente</DialogDescription>
                  </DialogHeader>
                  <POEditForm userId={sellerManageUser.user_id} currentPO={sellers.find(s => s.user_id === sellerManageUser.user_id)?.po_number || ''} onSave={async (po) => { await api.setPO(sellerManageUser.user_id, po); toast.success('PO-Nummer gespeichert'); setSellerManageUser(null); loadAll(); }} onClose={() => setSellerManageUser(null)} />
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export">
            <div className="grid md:grid-cols-3 gap-4">
              {[
                { type: 'rules', title: 'Provisionsregeln', desc: 'Alle Regeln mit Details und Status', icon: Settings },
                { type: 'codes', title: 'Rabattcodes', desc: 'Codes inklusive Nutzungsstatistik', icon: Tag },
                { type: 'transactions', title: 'Transaktionen', desc: 'Provisionsabrechnung aller Transaktionen', icon: FileText },
              ].map(({ type, title, desc, icon: Icon }) => (
                <Card key={type}>
                  <CardContent className="pt-5">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-2 bg-orange-50 dark:bg-orange-950 rounded-lg">
                        <Icon className="h-4 w-4 text-orange-500" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{title}</p>
                        <p className="text-xs text-muted-foreground">{desc}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => api.exportCsv(type)} data-testid={`export-csv-${type}`}>
                        <FileSpreadsheet className="h-3.5 w-3.5" />CSV
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 gap-1" onClick={() => api.exportPdf(type)} data-testid={`export-pdf-${type}`}>
                        <FileText className="h-3.5 w-3.5" />PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base">Transaktions-Export mit Filter</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="text-xs text-muted-foreground">Verkäufer</label>
                    <Select value={txnFilters.seller_id || "__all__"} onValueChange={v => setTxnFilters(f => ({ ...f, seller_id: v === "__all__" ? "" : v }))}>
                      <SelectTrigger className="w-44 mt-1"><SelectValue placeholder="Alle" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Alle Verkäufer</SelectItem>
                        {sellers.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Von</label>
                    <Input type="date" className="w-36 mt-1" value={txnFilters.date_from} onChange={e => setTxnFilters(f => ({ ...f, date_from: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Bis</label>
                    <Input type="date" className="w-36 mt-1" value={txnFilters.date_to} onChange={e => setTxnFilters(f => ({ ...f, date_to: e.target.value }))} />
                  </div>
                  <Button variant="outline" className="gap-1" onClick={() => api.exportCsv('transactions', { seller_id: txnFilters.seller_id, date_from: txnFilters.date_from, date_to: txnFilters.date_to })}>
                    <FileSpreadsheet className="h-4 w-4" />CSV Export
                  </Button>
                  <Button variant="outline" className="gap-1" onClick={() => api.exportPdf('transactions', { seller_id: txnFilters.seller_id, date_from: txnFilters.date_from, date_to: txnFilters.date_to })}>
                    <FileText className="h-4 w-4" />PDF Export
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ─── PO Edit Form ──────────────────────────────────────────────────────
function POEditForm({ userId, currentPO, onSave, onClose }) {
  const [po, setPo] = useState(currentPO || '');
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await onSave(po); } finally { setSaving(false); } };
  return (
    <div className="space-y-3">
      <Input placeholder="PO-Nummer eingeben..." value={po} onChange={e => setPo(e.target.value)} data-testid="po-number-input" />
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Abbrechen</Button>
        <Button onClick={save} disabled={saving}>{saving ? '...' : 'Speichern'}</Button>
      </DialogFooter>
    </div>
  );
}
